import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Bilingual (Arabic / English) i18n for the dashboard.
// - Default Arabic (target market is Iraqi merchants), switchable to English.
// - Persists choice in localStorage.
// - Layout is LOCKED to LTR — sidebar stays on the left for both languages.
//   Arabic text still renders right-to-left within elements via the browser's
//   bidi algorithm; only the page chrome and structural layout stay LTR.

export type Lang = 'ar' | 'en';

type Dict = Record<string, { ar: string; en: string }>;

const DICT: Dict = {
  // ─── Common ───────────────────────────────────────────────────────
  'brand.tagline': { ar: 'موظّفُ المبيعات الذي لا ينام', en: 'The sales rep that never sleeps' },
  'lang.switch': { ar: 'English', en: 'العربية' },
  'common.continue':  { ar: 'متابعة',           en: 'Continue' },
  'common.creating':  { ar: 'جارٍ الإنشاء…',    en: 'Creating…' },
  'common.loading':   { ar: 'جارٍ التحميل…',    en: 'Loading…' },
  'common.saving':    { ar: 'جارٍ الحفظ…',     en: 'Saving…' },
  'common.save':      { ar: 'حفظ التغييرات',   en: 'Save changes' },
  'common.saved':     { ar: 'محفوظ',           en: 'Saved' },
  'common.cancel':    { ar: 'إلغاء',           en: 'Cancel' },
  'common.delete':    { ar: 'حذف',             en: 'Delete' },
  'common.add':       { ar: 'إضافة',           en: 'Add' },
  'common.edit':      { ar: 'تعديل',           en: 'Edit' },
  'common.confirm':   { ar: 'تأكيد',           en: 'Confirm' },
  'common.search':    { ar: 'بحث',             en: 'Search' },
  'common.close':     { ar: 'إغلاق',           en: 'Close' },
  'common.optional':  { ar: 'اختياري',         en: 'Optional' },
  'common.all':       { ar: 'الكل',            en: 'All' },
  'common.none':      { ar: '—',               en: '—' },
  'common.unknown':   { ar: 'مجهول',           en: 'Unknown' },

  // ─── Sidebar / Topbar / Nav ───────────────────────────────────────
  'nav.overview':            { ar: 'الرئيسية',          en: 'Overview' },
  'nav.group.conversations': { ar: 'المحادثات',         en: 'Conversations' },
  'nav.inbox':               { ar: 'الوارد',            en: 'Inbox' },
  'nav.customers':           { ar: 'العملاء',           en: 'Customers' },
  'nav.group.store':         { ar: 'المتجر',            en: 'Store' },
  'nav.products':            { ar: 'المنتجات',          en: 'Products' },
  'nav.orders':              { ar: 'الطلبات',           en: 'Orders' },
  'nav.group.autoreply':     { ar: 'الرد التلقائي',    en: 'Auto-reply' },
  'nav.social':              { ar: 'المنشورات',         en: 'Posts' },
  'nav.group.ai':            { ar: 'الذكاء الاصطناعي',  en: 'AI' },
  'nav.sandbox':             { ar: 'التجربة',           en: 'Playground' },
  'nav.knowledge':           { ar: 'قاعدة المعرفة',     en: 'Knowledge' },
  'nav.voice':               { ar: 'نبرة العلامة',      en: 'Brand voice' },
  'nav.zones':               { ar: 'مناطق التوصيل',     en: 'Delivery zones' },
  'nav.group.settings':      { ar: 'الإعدادات',         en: 'Settings' },
  'nav.channels':            { ar: 'إنستغرام وماسنجر',  en: 'Instagram & Messenger' },
  'nav.whatsapp':            { ar: 'تحويل واتساب',     en: 'WhatsApp handoff' },
  'nav.team':                { ar: 'الفريق',            en: 'Team' },
  'nav.credits':             { ar: 'رصيد الذكاء',       en: 'AI credits' },
  'nav.billing':             { ar: 'الفوترة',           en: 'Billing' },
  'nav.analytics':           { ar: 'التحليلات',         en: 'Analytics' },
  'side.creditpill':         { ar: 'رصيد الذكاء',       en: 'AI credits' },
  'side.signout':            { ar: 'تسجيل الخروج',     en: 'Sign out' },
  'topbar.search.ph':        { ar: 'بحث سريع…',         en: 'Quick search…' },

  // ─── AI status / channels / roles / order status ──────────────────
  'ai.active':         { ar: 'الذكاء يردّ',  en: 'AI replying' },
  'ai.paused_auto':    { ar: 'محوّلة لك',     en: 'Escalated' },
  'ai.paused_manual':  { ar: 'تتولّاها',      en: 'You handle' },
  'ai.disabled':       { ar: 'موقوف',         en: 'Off' },
  'channel.instagram': { ar: 'إنستغرام',      en: 'Instagram' },
  'channel.facebook':  { ar: 'ماسنجر',        en: 'Messenger' },
  'channel.whatsapp':  { ar: 'واتساب',        en: 'WhatsApp' },
  'role.owner':        { ar: 'مالك',          en: 'Owner' },
  'role.admin':        { ar: 'مدير',          en: 'Admin' },
  'role.agent':        { ar: 'موظف',          en: 'Agent' },
  'role.viewer':       { ar: 'مشاهد',         en: 'Viewer' },
  'orderstatus.pending':    { ar: 'قيد الانتظار',  en: 'Pending' },
  'orderstatus.confirmed':  { ar: 'مؤكّد',         en: 'Confirmed' },
  'orderstatus.preparing':  { ar: 'قيد التجهيز',  en: 'Preparing' },
  'orderstatus.dispatched': { ar: 'قيد الشحن',    en: 'Dispatched' },
  'orderstatus.delivered':  { ar: 'تمّ التوصيل',   en: 'Delivered' },
  'orderstatus.cancelled':  { ar: 'ملغى',         en: 'Cancelled' },
  'orderstatus.returned':   { ar: 'مُرجَع',        en: 'Returned' },
  'embed.embedded': { ar: 'مُفهرَس',         en: 'Indexed' },
  'embed.failed':   { ar: 'فشل الفهرسة',     en: 'Indexing failed' },
  'embed.pending':  { ar: 'قيد المعالجة',    en: 'Processing' },
  'status.active':   { ar: 'نشط',     en: 'Active' },
  'status.inactive': { ar: 'موقوف',   en: 'Inactive' },

  // ─── Overview ─────────────────────────────────────────────────────
  'page.overview.welcome':  { ar: 'أهلاً',              en: 'Welcome' },
  'page.overview.sub':      { ar: 'نظرة سريعة على عملك اليوم.', en: 'A quick look at your business today.' },
  'page.overview.aiactive': { ar: 'الذكاء يعمل الآن — يردّ على رسائلك وتعليقاتك تلقائياً.', en: 'AI is active — handling your DMs and comments automatically.' },
  'page.overview.quick.products': { ar: 'إدارة المنتجات', en: 'Manage products' },
  'page.overview.quick.inbox':    { ar: 'فتح الوارد',    en: 'Open inbox' },
  'page.overview.quick.channels': { ar: 'ربط الحسابات',  en: 'Connect channels' },

  // ─── Inbox ────────────────────────────────────────────────────────
  'page.inbox.title':         { ar: 'الوارد',                              en: 'Inbox' },
  'page.inbox.count':         { ar: 'محادثة',                              en: 'conversations' },
  'page.inbox.unreadBadge':   { ar: 'غير مقروءة',                          en: 'Unread' },
  'page.inbox.search.ph':     { ar: 'ابحث بالاسم أو الهاتف أو نص الرسالة…', en: 'Search by name, phone, or message text…' },
  'page.inbox.unreadOnly':    { ar: 'غير مقروءة فقط',                       en: 'Unread only' },
  'page.inbox.aistatus.all':  { ar: 'حالة الذكاء: الكل',                    en: 'AI status: any' },
  'page.inbox.empty.h':       { ar: 'لا توجد محادثات',                      en: 'No conversations yet' },
  'page.inbox.empty.p':       { ar: 'عند وصول أول رسالة، ستظهر هنا.',      en: 'Your first conversation will show up here.' },
  'time.now':  { ar: 'الآن', en: 'now' },
  'time.min':  { ar: 'د',    en: 'm' },
  'time.hour': { ar: 'س',    en: 'h' },
  'time.day':  { ar: 'ي',    en: 'd' },

  // ─── Products ─────────────────────────────────────────────────────
  'page.products.title':  { ar: 'المنتجات', en: 'Products' },
  'page.products.sub':    { ar: 'الكتالوج الذي يبيعه الذكاء. كل منتج يُفهرَس نصّاً وصورةً ليُعثَر عليه بالوصف أو بالصورة.', en: 'Your catalog. Each product is indexed by text and image so the AI can find it.' },
  'page.products.import': { ar: 'استيراد CSV', en: 'Import CSV' },
  'page.products.new':    { ar: 'إضافة منتج',  en: 'Add product' },
  'page.products.count':  { ar: 'منتج',         en: 'products' },
  'page.products.cap':    { ar: 'يُعرَض حتى ٥٠٠ منتج', en: 'Showing up to 500 products' },
  'page.products.empty.h': { ar: 'لا توجد منتجات بعد', en: 'No products yet' },
  'page.products.empty.p': { ar: 'أضف أول منتج حتى يكون لدى الذكاء ما يبيعه.', en: 'Add your first product so the AI has something to sell.' },

  // ─── Orders ───────────────────────────────────────────────────────
  'page.orders.title': { ar: 'الطلبات', en: 'Orders' },
  'page.orders.sub':   { ar: 'طلبات الدفع عند الاستلام التي أنشأها الذكاء داخل المحادثات — مع خصم المخزون تلقائياً.', en: 'COD orders the AI created inside conversations — with automatic stock decrement.' },
  'page.orders.tab.all':    { ar: 'الكل',     en: 'All' },
  'page.orders.tab.active': { ar: 'نشطة',     en: 'Active' },
  'page.orders.tab.done':   { ar: 'مكتملة',   en: 'Completed' },
  'page.orders.col.id':       { ar: 'رقم الطلب', en: 'Order #' },
  'page.orders.col.customer': { ar: 'العميل',    en: 'Customer' },
  'page.orders.col.items':    { ar: 'المنتجات',  en: 'Items' },
  'page.orders.col.total':    { ar: 'الإجمالي',  en: 'Total' },
  'page.orders.col.status':   { ar: 'الحالة',    en: 'Status' },
  'page.orders.col.date':     { ar: 'التاريخ',   en: 'Date' },
  'page.orders.empty':        { ar: 'لا توجد طلبات بعد.', en: 'No orders yet.' },

  // ─── Customers ────────────────────────────────────────────────────
  'page.customers.title':       { ar: 'العملاء', en: 'Customers' },
  'page.customers.sub':         { ar: 'قاعدة بيانات لكلّ من راسلك — تُبنى تلقائياً وتتثرى مع كلّ طلب.', en: 'A directory of everyone who messaged you — built automatically, enriched by every order.' },
  'page.customers.search.ph':   { ar: 'بحث بالاسم أو الهاتف…', en: 'Search by name or phone…' },
  'page.customers.col.customer':    { ar: 'العميل',     en: 'Customer' },
  'page.customers.col.phone':       { ar: 'الهاتف',     en: 'Phone' },
  'page.customers.col.orders':      { ar: 'الطلبات',    en: 'Orders' },
  'page.customers.col.appts':       { ar: 'المواعيد',   en: 'Appts' },
  'page.customers.col.tags':        { ar: 'الوسوم',     en: 'Tags' },
  'page.customers.col.lastContact': { ar: 'آخر تواصل',  en: 'Last contact' },
  'page.customers.empty':           { ar: 'لا يوجد عملاء بعد.', en: 'No customers yet.' },

  // ─── Social posts ─────────────────────────────────────────────────
  'page.social.title':       { ar: 'الرد التلقائي على التعليقات', en: 'Comment auto-reply' },
  'page.social.sub':         { ar: 'اربط كلّ منشور بمنتجاته، وسيقوم الذكاء بالرد على المعلّقين تلقائياً ومراسلتهم باسم المنتج وسعره — مثل ManyChat لكن أذكى.', en: 'Link each post to its products and the AI auto-replies to comments and DMs the commenter the product name and price.' },
  'page.social.gate.h':      { ar: 'اربط إنستغرام وفيسبوك أولاً', en: 'Connect Instagram & Facebook first' },
  'page.social.gate.p':      { ar: 'للرد التلقائي على التعليقات نحتاج ربط صفحتك على فيسبوك وحساب إنستغرام للأعمال.', en: 'To auto-reply to comments we need your Facebook Page + Instagram Business account connected.' },
  'page.social.gate.cta':    { ar: 'ربط الحسابات', en: 'Connect accounts' },
  'page.social.stat.comments': { ar: 'تعليقات (٣٠ يوم)', en: 'Comments (30d)' },
  'page.social.stat.dms':      { ar: 'رسائل أُرسلت',     en: 'DMs sent' },
  'page.social.stat.ignored':  { ar: 'تم تجاهلها',       en: 'Ignored' },
  'page.social.stat.spam':     { ar: 'سبام مُصفّى',      en: 'Spam filtered' },
  'page.social.empty.h':       { ar: 'لا توجد منشورات',   en: 'No posts' },
  'page.social.empty.p':       { ar: 'انشر شيئاً على إنستغرام أو فيسبوك ثم حدّث الصفحة.', en: 'Publish something on Instagram or Facebook and refresh.' },
  'page.social.loading':       { ar: 'جارٍ تحميل منشوراتك…', en: 'Loading your posts…' },
  'page.social.tile.linked':   { ar: 'مفعّل',  en: 'On' },
  'page.social.tile.off':      { ar: 'موقوف',  en: 'Off' },
  'page.social.tile.link':     { ar: 'ربط',    en: 'Link' },
  'page.social.tile.products': { ar: 'منتج',   en: 'products' },
  'page.social.modal.open':    { ar: 'فتح',         en: 'Open' },
  'page.social.modal.noCap':   { ar: 'بدون وصف',    en: 'No caption' },
  'page.social.modal.products':     { ar: 'المنتجات المعروضة في هذا المنشور', en: 'Products shown in this post' },
  'page.social.modal.productsHint': { ar: 'اربط منتجاً واحداً على الأقل حتى يعرف الذكاء ما يقتبسه في الرد.', en: 'Link at least one product so the AI knows what to quote.' },
  'page.social.modal.enable':       { ar: 'الرد التلقائي على التعليقات', en: 'Auto-reply to comments' },
  'page.social.modal.enableHint':   { ar: 'عند التفعيل، يرد الذكاء على المعلّقين ويراسلهم بالخاص.', en: 'When on, the AI replies to commenters and DMs them.' },
  'page.social.modal.limit':        { ar: 'أقصى عدد ردود تلقائية لكل منشور', en: 'Max auto-replies per post' },
  'page.social.modal.limitHint':    { ar: 'يرد الذكاء على أول مجموعة فقط — يحمي حسابك من التكرار وحدود ميتا.', en: 'The AI only replies to the first batch — protects your account from looking spammy.' },
  'page.social.modal.dm':           { ar: 'الرسالة الخاصة (DM)', en: 'Private message (DM)' },
  'page.social.modal.dmPh':         { ar: 'اتركه فارغاً ليكتب الذكاء الرسالة تلقائياً، أو اكتب رسالتك…', en: 'Leave empty for the AI to write it, or type yours…' },
  'page.social.modal.public':       { ar: 'الرد العلني تحت التعليق', en: 'Public reply under the comment' },
  'page.social.modal.publicPh':     { ar: 'اتركه فارغاً للرد الافتراضي «أرسلت لك التفاصيل بالخاص»', en: 'Leave empty for default "Sent you the details"' },
  'page.social.modal.placeholders': { ar: 'استخدم {product} لاسم المنتج، {price} للسعر، {name} لاسم العميل.', en: 'Use {product} for product name, {price} for price, {name} for the customer name.' },
  'page.social.modal.template':     { ar: 'قالب', en: 'Template' },
  'page.social.modal.save':         { ar: 'حفظ الإعدادات', en: 'Save settings' },
  'page.social.modal.unlink':       { ar: 'إلغاء الربط',   en: 'Unlink' },
  'page.social.search.products.ph': { ar: 'ابحث عن منتج…', en: 'Search products…' },
  'page.social.search.products.empty': { ar: 'لا توجد منتجات مطابقة.', en: 'No matching products.' },

  // ─── Knowledge ────────────────────────────────────────────────────
  'page.knowledge.title': { ar: 'قاعدة المعرفة', en: 'Knowledge base' },
  'page.knowledge.sub':   { ar: 'كل ما يعرفه الذكاء عن متجرك — أسئلة شائعة، سياسات، ومستندات يستشهد بها في الردود.', en: 'What the AI knows about your store — FAQs, policies, and documents it can cite.' },
  'page.knowledge.faq.h':       { ar: 'الأسئلة الشائعة', en: 'FAQs' },
  'page.knowledge.faq.count':   { ar: 'من أصل ٢٠٠',    en: 'of 200' },
  'page.knowledge.faq.q':       { ar: 'السؤال (بكلمات العميل)', en: "Question (in the customer's words)" },
  'page.knowledge.faq.qPh':     { ar: 'مثلاً: شكم سعر التوصيل لبغداد؟', en: 'e.g. How much is delivery to Baghdad?' },
  'page.knowledge.faq.a':       { ar: 'الجواب الذي يقدّمه الذكاء', en: 'Answer the AI should give' },
  'page.knowledge.faq.aPh':     { ar: 'مثلاً: التوصيل داخل بغداد ٥ آلاف دينار، يصل خلال يوم.', en: 'e.g. Delivery to Baghdad is 5,000 IQD, arrives in a day.' },
  'page.knowledge.faq.priority':{ ar: 'أولوية', en: 'Priority' },
  'page.knowledge.faq.add':     { ar: 'إضافة سؤال', en: 'Add FAQ' },
  'page.knowledge.faq.adding':  { ar: 'جارٍ الإضافة…', en: 'Adding…' },
  'page.knowledge.faq.delConfirm':{ ar: 'حذف هذا السؤال؟', en: 'Delete this FAQ?' },
  'page.knowledge.faq.empty.h': { ar: 'لا توجد أسئلة بعد', en: 'No FAQs yet' },
  'page.knowledge.faq.empty.p': { ar: 'أضف أسئلتك الأكثر تكراراً ليُجيب الذكاء تلقائياً بكلماتك أنت.', en: 'Add your common questions so the AI can answer in your own words.' },
  'page.knowledge.doc.h':       { ar: 'المستندات', en: 'Documents' },
  'page.knowledge.doc.sub':     { ar: 'ملفات يستند إليها الذكاء — كتالوج، سياسات، أدلة استخدام.', en: 'Files the AI cites — catalog, policies, manuals.' },
  'page.knowledge.doc.used':    { ar: 'التخزين المستخدم', en: 'Storage used' },
  'page.knowledge.doc.empty.h': { ar: 'لا توجد مستندات', en: 'No documents' },
  'page.knowledge.doc.empty.p': { ar: 'بإمكانك رفع كتالوج PDF أو ملفات سياسات لاحقاً.', en: 'You can upload PDFs and policy docs later.' },
  'page.knowledge.faq.lang.ar': { ar: 'عربي',  en: 'Arabic' },
  'page.knowledge.faq.lang.en': { ar: 'English', en: 'English' },
  'page.knowledge.priorityBadge': { ar: 'أولوية', en: 'Priority' },

  // ─── BrandVoice ───────────────────────────────────────────────────
  'page.voice.title':     { ar: 'نبرة العلامة', en: 'Brand voice' },
  'page.voice.sub':       { ar: 'كيف يتحدث الذكاء مع عملائك. اكتب التعليمات كأنك تشرح لزميل جديد.', en: 'How the AI talks to your customers. Write instructions like you would for a new teammate.' },
  'page.voice.instr':     { ar: 'تعليمات النبرة', en: 'Voice instructions' },
  'page.voice.instr.ph':  { ar: 'مثلاً: تحدّث بشكل ودود وعفوي باللهجة العراقية. لا تستخدم العربية الفصحى. ناد العملاء المتكررين بـ«حبيبي». لا تضغط على البيع.', en: 'e.g. Talk casually in Iraqi Arabic. Avoid formal Arabic. Call regulars "habibi". Don\'t hard-sell.' },
  'page.voice.instr.hint':{ ar: 'كلّما كانت التعليمات أوضح، انعكس صوت علامتك بدقة أكبر في الردود.', en: 'The clearer the instructions, the more accurately your brand voice shows up in replies.' },
  'page.voice.upsell':    { ar: 'شدّة الاقتراحات الإضافية (Upsell)', en: 'Upsell aggressiveness' },
  'page.voice.speed':     { ar: 'سرعة الردّ', en: 'Response speed' },
  'page.voice.preset.friendly': { ar: 'ودود', en: 'Friendly' },
  'page.voice.preset.luxury':   { ar: 'فاخر', en: 'Luxury' },
  'page.voice.preset.fast':     { ar: 'سريع', en: 'Fast' },
  'voice.upsell.off':    { ar: 'موقوف',  en: 'Off' },
  'voice.upsell.low':    { ar: 'خفيف',   en: 'Low' },
  'voice.upsell.medium': { ar: 'متوسّط', en: 'Medium' },
  'voice.upsell.high':   { ar: 'قوي',     en: 'High' },
  'voice.upsell.hint.off':    { ar: 'لا اقتراحات إضافية', en: 'No upsell suggestions' },
  'voice.upsell.hint.low':    { ar: 'اقتراح واحد فقط عند الإغلاق', en: 'One suggestion at checkout' },
  'voice.upsell.hint.medium': { ar: 'إضافات منطقية حسب السلة', en: 'Smart add-ons based on the cart' },
  'voice.upsell.hint.high':   { ar: 'يقترح ويذكّر بأكثر من فرصة', en: 'Suggests and follows up multiple times' },
  'voice.speed.instant': { ar: 'فوري',   en: 'Instant' },
  'voice.speed.natural': { ar: 'طبيعي',  en: 'Natural' },
  'voice.speed.slow':    { ar: 'بطيء',   en: 'Slow' },
  'voice.speed.hint.instant': { ar: 'يردّ مباشرة', en: 'Replies immediately' },
  'voice.speed.hint.natural': { ar: 'يحاكي إنساناً يكتب', en: 'Mimics a human typing' },
  'voice.speed.hint.slow':    { ar: 'تأخير متعمَّد للمشغولين', en: 'Deliberate delay for busy users' },

  // ─── Delivery zones ───────────────────────────────────────────────
  'page.zones.title':       { ar: 'مناطق التوصيل', en: 'Delivery zones' },
  'page.zones.sub':         { ar: 'حدّد كلفة التوصيل لكلّ محافظة. يستخدمها الذكاء لتقدير المبلغ الإجمالي تلقائياً في كل طلب.', en: 'Set a delivery fee for each governorate. The AI uses this to quote totals automatically.' },
  'page.zones.empty.h':     { ar: 'لا توجد مناطق توصيل بعد', en: 'No delivery zones yet' },
  'page.zones.empty.p':     { ar: 'استخدم الإضافة السريعة بالأسفل لاختيار المحافظات التي توصّل إليها.', en: 'Use the quick-add chips below to pick the governorates you ship to.' },
  'page.zones.name':        { ar: 'اسم المنطقة',         en: 'Zone name' },
  'page.zones.name.ph':     { ar: 'مثلاً: بغداد',         en: 'e.g. Baghdad' },
  'page.zones.aliases':     { ar: 'أسماء بديلة (تساعد الذكاء على التعرّف)', en: 'Aliases (help the AI recognize the zone)' },
  'page.zones.aliases.ph':  { ar: 'Baghdad، بغداد، العاصمة', en: 'Baghdad, capital' },
  'page.zones.fee':         { ar: 'كلفة التوصيل',         en: 'Delivery fee' },
  'page.zones.currency':    { ar: 'العملة',                en: 'Currency' },
  'page.zones.eta':         { ar: 'مدّة التوصيل التقديرية (ساعات)', en: 'Estimated ETA (hours)' },
  'page.zones.new':         { ar: 'محافظة جديدة',         en: 'New zone' },
  'page.zones.quickAdd':    { ar: 'إضافة سريعة — المحافظات العراقية', en: 'Quick-add — Iraqi governorates' },

  // ─── Channels (Connect Meta) ──────────────────────────────────────
  'page.channels.title':    { ar: 'إنستغرام وماسنجر', en: 'Instagram & Messenger' },
  'page.channels.sub':      { ar: 'اربط صفحتك على فيسبوك وحساب إنستغرام للأعمال — يتولّى الذكاء الرسائل والتعليقات تلقائياً عبر Graph API مباشرة.', en: 'Connect your Facebook Page and Instagram Business account — the AI handles DMs and comments directly via Graph API.' },
  'page.channels.connect':  { ar: 'الربط عبر فيسبوك',    en: 'Connect with Facebook' },
  'page.channels.loading':  { ar: 'جارٍ تحميل SDK…',      en: 'Loading SDK…' },
  'page.channels.notSet':   { ar: 'لم يُضبط VITE_META_APP_ID', en: 'Set VITE_META_APP_ID' },
  'page.channels.empty.h':  { ar: 'لا توجد حسابات مربوطة', en: 'No connected accounts' },
  'page.channels.empty.p':  { ar: 'اربط صفحتك على فيسبوك (المُرتبطة بحساب إنستغرام للأعمال) ليبدأ الذكاء بالردّ على الرسائل والتعليقات.', en: 'Connect your Facebook Page (linked to an Instagram Business account) so the AI can reply to messages and comments.' },
  'page.channels.req.h':    { ar: 'المتطلّبات', en: 'Requirements' },
  'page.channels.req.1':    { ar: 'أن تكون صفحتك على فيسبوك مرتبطة بحساب إنستغرام للأعمال أو لمنشئي المحتوى.', en: 'Your Facebook Page must be linked to an Instagram Business or Creator account.' },
  'page.channels.req.2':    { ar: 'أن تكون مديراً (Admin) أو محرّراً للصفحة على فيسبوك.', en: 'You must be an Admin or Editor of the Facebook Page.' },
  'page.channels.req.3':    { ar: 'أثناء كون التطبيق في وضع التطوير، يمكن لحسابات المطوّرين/المختبرين فقط الربط.', en: 'While the app is in development mode, only developer/tester accounts can connect.' },
  'page.channels.tokenExpires': { ar: 'ينتهي', en: 'Expires' },
  'page.channels.autoReply':    { ar: 'الرد التلقائي على التعليقات', en: 'Auto-reply to comments' },
  'page.channels.autoReplyHint':{ ar: 'يرسل الذكاء رسالة خاصة لمن يعلّق على منشوراتك.', en: 'The AI privately DMs anyone who comments on your posts.' },
  'page.channels.disconnect':   { ar: 'إلغاء الربط', en: 'Disconnect' },
  'page.channels.disConfirm':   { ar: 'إلغاء ربط هذه الصفحة؟ ستتوقف الردود التلقائية فوراً.', en: 'Disconnect this page? Auto-replies will stop immediately.' },
  'page.channels.saving':       { ar: 'جارٍ حفظ الربط والاشتراك في الـwebhooks…', en: 'Saving connection and subscribing webhooks…' },
  'page.channels.err.sdk':      { ar: 'لم يُحمَّل SDK فيسبوك بعد، جرّب بعد لحظة.', en: "Facebook SDK isn't ready yet — try again in a moment." },
  'page.channels.err.declined': { ar: 'رفضت منح الصلاحيات. أعد الربط وامنح كل الصلاحيات المطلوبة.', en: 'You declined permissions. Reconnect and grant all requested scopes.' },
  'page.channels.err.incomplete':{ ar: 'لم يكتمل تسجيل الدخول. حاول مرة أخرى.', en: 'Login was not completed. Try again.' },

  // ─── WhatsApp handoff (SupportChannels) ───────────────────────────
  'page.whatsapp.title':  { ar: 'تحويل واتساب', en: 'WhatsApp handoff' },
  'page.whatsapp.sub':    { ar: 'حين يحتاج العميل إنساناً، يحوّله الذكاء عبر رابط wa.me إلى رقم واتساب الذي تختاره — لا حساب واتساب أعمال مطلوب.', en: 'When a customer needs a human, the AI sends them a wa.me link to your chosen WhatsApp number — no WhatsApp Business account required.' },
  'page.whatsapp.phone':  { ar: 'رقم واتساب الدعم', en: 'Support WhatsApp number' },
  'page.whatsapp.phone.ph':{ ar: '+9647xxxxxxxxx', en: '+9647xxxxxxxxx' },
  'page.whatsapp.phone.hint':{ ar: 'الرقم بالصيغة الدولية يبدأ بـ +.', en: 'International format starting with +.' },
  'page.whatsapp.msg':    { ar: 'الرسالة الافتراضية', en: 'Default prefilled message' },
  'page.whatsapp.msg.ph': { ar: 'مثلاً: مرحباً، أحتاج مساعدة بطلبي رقم {order_id}', en: 'e.g. Hi, I need help with order {order_id}' },
  'page.whatsapp.msg.hint': { ar: 'تُعرض في واتساب فور فتح الرابط. اتركها فارغة للسماح للذكاء بكتابتها.', en: 'Pre-filled in WhatsApp when the link opens. Leave blank to let the AI write it.' },
  'page.whatsapp.preview':{ ar: 'معاينة الرابط',     en: 'Link preview' },
  'page.whatsapp.test':   { ar: 'اختبار الفتح',      en: 'Test open' },
  'page.whatsapp.empty.h':{ ar: 'لم يُضبط رقم بعد',  en: 'No number set yet' },
  'page.whatsapp.empty.p':{ ar: 'حدّد رقماً ليبدأ الذكاء بتحويل العملاء عند الحاجة.', en: 'Set a number so the AI can hand off when needed.' },

  // ─── Team ─────────────────────────────────────────────────────────
  'page.team.title': { ar: 'الفريق', en: 'Team' },
  'page.team.sub':   { ar: 'الأشخاص الذين يصلون لهذه اللوحة. للمالك صلاحيات كاملة، والمدير يدير المنتجات والطلبات، والموظف يردّ من الوارد، والمشاهد يقرأ فقط.', en: 'People with access to this dashboard. Owners have full control, admins manage products & orders, agents reply from the inbox, viewers read only.' },
  'page.team.empty.h': { ar: 'لا يوجد أعضاء بعد', en: 'No team members yet' },
  'page.team.empty.p': { ar: 'ستظهر هنا قائمة أعضاء فريقك عند إضافتهم.', en: 'Your team members will appear here once added.' },
  'page.team.col.member': { ar: 'العضو', en: 'Member' },
  'page.team.col.email':  { ar: 'البريد', en: 'Email' },
  'page.team.col.role':   { ar: 'الدور', en: 'Role' },
  'page.team.col.status': { ar: 'الحالة', en: 'Status' },
  'page.team.col.since':  { ar: 'منذ', en: 'Since' },
  'page.team.active':     { ar: 'مفعّل', en: 'Active' },
  'page.team.inactive':   { ar: 'موقوف', en: 'Off' },

  // ─── Credits ──────────────────────────────────────────────────────
  'page.credits.title':   { ar: 'رصيد الذكاء', en: 'AI credits' },
  'page.credits.sub':     { ar: 'كل رسالة يردّها الذكاء تكلّف رصيداً واحداً. اشتراكك يتضمّن ٥٠٠٠ شهرياً، والشحنات الإضافية لا تنتهي.', en: 'Each AI-handled message costs 1 credit. Your plan includes 5,000 per month; top-ups never expire.' },
  'page.credits.remaining':       { ar: 'الرصيد المتبقّي', en: 'Remaining' },
  'page.credits.remainingDetail': { ar: 'رصيد متاح للاستخدام', en: 'Credits available to use' },
  'page.credits.low':             { ar: 'رصيدك قارب على النفاد — سيتوقف الذكاء عند الصفر', en: 'Running low — the AI pauses when this hits zero' },
  'page.credits.subStatus':       { ar: 'اشتراك', en: 'Subscription' },
  'page.credits.renewal':         { ar: 'التجديد', en: 'Renews' },
  'page.credits.monthly':         { ar: 'الاشتراك الشهري', en: 'Monthly plan' },
  'page.credits.monthlyOf':       { ar: 'من', en: 'of' },
  'page.credits.monthlyResets':   { ar: 'يتجدّد كل شهر مع الاشتراك.', en: 'Resets every month with your plan.' },
  'page.credits.topup':           { ar: 'شحنات إضافية', en: 'Top-up balance' },
  'page.credits.topupHint':       { ar: 'لا تنتهي. تُستهلَك بعد نفاد رصيد الاشتراك.', en: 'Never expire. Used after the monthly bucket runs out.' },
  'page.credits.buy.h':           { ar: 'شراء رصيد إضافي', en: 'Buy more credits' },
  'page.credits.buy.hint':        { ar: 'الشحنات تُضاف لرصيد دائم لا ينتهي.', en: 'Top-ups go to a permanent balance that never expires.' },
  'page.credits.history':         { ar: 'سجلّ المشتريات', en: 'Purchase history' },
  'page.credits.history.empty.h': { ar: 'لا توجد مشتريات بعد', en: 'No purchases yet' },
  'page.credits.history.empty.p': { ar: 'اشتراكك الشهري يكفي معظم التجّار — اشترِ المزيد عند الحاجة.', en: 'Your monthly plan covers most merchants — top up when you need a boost.' },
  'page.credits.col.pack':    { ar: 'الباقة',  en: 'Pack' },
  'page.credits.col.credits': { ar: 'الرصيد',  en: 'Credits' },
  'page.credits.col.price':   { ar: 'السعر',   en: 'Price' },
  'page.credits.col.status':  { ar: 'الحالة',  en: 'Status' },
  'page.credits.col.date':    { ar: 'التاريخ', en: 'Date' },
  'page.credits.markPaid':    { ar: 'تأكيد الدفع', en: 'Confirm payment' },
  'page.credits.pack.popular':  { ar: 'الأكثر طلباً', en: 'Most popular' },
  'page.credits.pack.best':     { ar: 'أفضل قيمة',    en: 'Best value' },
  'page.credits.pack.unit':     { ar: 'رصيد',         en: 'credits' },
  'page.credits.pack.perCredit':{ ar: '/ رصيد',        en: '/ credit' },
  'page.credits.pack.buy':      { ar: 'شراء',          en: 'Buy' },
  'page.credits.pack.starting': { ar: 'جارٍ البدء…',   en: 'Starting…' },
  'page.credits.modal.title':     { ar: 'تأكيد الدفع', en: 'Confirm payment' },
  'page.credits.modal.sub':       { ar: 'عند استلام المبلغ، أكّد هنا لإيداع الرصيد في حسابك.', en: 'Once payment is received, confirm here to credit your account.' },
  'page.credits.modal.confirming':{ ar: 'جارٍ التأكيد…', en: 'Confirming…' },
  'page.credits.modal.empty':     { ar: 'لا توجد تفاصيل للعملية.', en: 'No purchase details.' },
  'page.credits.purchase.paid':     { ar: 'مدفوع',     en: 'Paid' },
  'page.credits.purchase.pending':  { ar: 'قيد التأكيد', en: 'Pending' },
  'page.credits.purchase.failed':   { ar: 'فشل',       en: 'Failed' },
  'page.credits.purchase.refunded': { ar: 'مُستردّ',   en: 'Refunded' },

  // ─── Billing ──────────────────────────────────────────────────────
  'page.billing.title': { ar: 'الفوترة', en: 'Billing' },
  'page.billing.sub':   { ar: 'حالة اشتراكك، استهلاك الرصيد لهذه الدورة، وفواتير الدفع.', en: 'Subscription status, credit usage this cycle, and invoices.' },
  'page.billing.sub.h':       { ar: 'حالة الاشتراك', en: 'Subscription status' },
  'page.billing.sub.period':  { ar: 'الدورة الحالية', en: 'Current period' },
  'page.billing.trial.ends':  { ar: 'التجربة تنتهي', en: 'Trial ends' },
  'page.billing.stat.used':       { ar: 'رصيد مُستخدَم',       en: 'Credits used' },
  'page.billing.stat.remaining':  { ar: 'متبقّي من الاشتراك',  en: 'Remaining from plan' },
  'page.billing.stat.topup':      { ar: 'شحنات إضافية',         en: 'Top-up balance' },
  'page.billing.usage':           { ar: 'استهلاك الدورة الحالية', en: 'This-cycle usage' },
  'page.billing.empty.h':         { ar: 'لا يوجد اشتراك نشط',     en: 'No active subscription' },
  'page.billing.empty.p':         { ar: 'ستظهر تفاصيل اشتراكك هنا.', en: 'Your subscription details will appear here.' },
  'page.billing.invoices.h':      { ar: 'الفواتير', en: 'Invoices' },
  'page.billing.invoices.empty.h':{ ar: 'لا توجد فواتير بعد', en: 'No invoices yet' },
  'page.billing.invoices.empty.p':{ ar: 'ستظهر فواتير الدفع هنا بمجرّد توليدها.', en: 'Invoices will appear here once issued.' },
  'page.billing.invoices.col.id':     { ar: 'الرقم',  en: 'Invoice #' },
  'page.billing.invoices.col.amount': { ar: 'المبلغ', en: 'Amount' },
  'page.billing.invoices.col.status': { ar: 'الحالة', en: 'Status' },
  'page.billing.invoices.col.date':   { ar: 'التاريخ', en: 'Date' },
  'page.billing.payment.h':       { ar: 'طريقة الدفع', en: 'Payment method' },
  'page.billing.payment.p':       { ar: 'الدفع حالياً يتمّ بالتأكيد اليدوي للحوالات والكاش. عند تفعيل بطاقات الدفع ستظهر بطاقتك الافتراضية وخيارات التحديث هنا.', en: 'Payments are currently confirmed manually (cash / bank transfer). When card billing is enabled, your default card and update options will appear here.' },
  'billing.status.active':   { ar: 'نشط',     en: 'Active' },
  'billing.status.trialing': { ar: 'تجريبي',  en: 'Trialing' },
  'billing.status.past_due': { ar: 'متأخّر',  en: 'Past due' },
  'billing.status.cancelled':{ ar: 'مُلغى',   en: 'Cancelled' },
  'billing.status.paused':   { ar: 'موقوف',   en: 'Paused' },

  // ─── Analytics ────────────────────────────────────────────────────
  'page.analytics.title':   { ar: 'التحليلات', en: 'Analytics' },
  'page.analytics.sub':     { ar: 'نظرة سريعة على الحجم والأداء — تتعمّق التقارير مع تراكم البيانات.', en: 'A quick read of volume and performance — reports deepen as data accumulates.' },
  'page.analytics.stat.convs7d':   { ar: 'محادثات (٧ أيام)', en: 'Conversations (7d)' },
  'page.analytics.stat.aiPct':     { ar: 'نسبة تولّي الذكاء', en: 'AI-handled %' },
  'page.analytics.stat.comments':  { ar: 'تعليقات معالَجة (٣٠ يوم)', en: 'Comments handled (30d)' },
  'page.analytics.stat.dms':       { ar: 'رسائل خاصة تلقائية', en: 'Auto DMs' },
  'page.analytics.stat.credits':   { ar: 'رصيد مُستخدَم', en: 'Credits used' },
  'page.analytics.stat.esc':       { ar: 'تحويلات للإنسان', en: 'Escalations' },
  'page.analytics.summary.h':      { ar: 'ملخّص الأسبوع', en: 'This week' },
  'page.analytics.summary.ai':     { ar: 'محادثات يتولّاها الذكاء', en: 'Conversations the AI handled' },
  'page.analytics.summary.esc':    { ar: 'محادثات بانتظار ردّك', en: 'Awaiting your reply' },
  'page.analytics.summary.spam':   { ar: 'تعليقات تجاهلها الفلتر', en: 'Comments the filter skipped' },
  'page.analytics.summary.del':    { ar: 'سبام محذوف', en: 'Spam removed' },
  'page.analytics.more.h':         { ar: 'المزيد من التقارير قريباً', en: 'More reports coming' },
  'page.analytics.more.p':         { ar: 'تقارير معدّل الإغلاق، الإيرادات اليومية، توزيع الردود حسب القناة، وأبطأ الردود — تظهر بعد أسبوعين من تراكم بياناتك.', en: 'Close-rate, daily revenue, channel mix, and slowest replies — once you have two weeks of data.' },

  // ─── Auth (kept from previous build) ──────────────────────────────
  'auth.signin.title': { ar: 'تسجيل الدخول', en: 'Sign in' },
  'auth.signup.title': { ar: 'إنشاء حساب', en: 'Create your account' },
  'auth.hero.line1': { ar: 'ذكاء اصطناعي يردّ على عملائك', en: 'AI that answers your customers' },
  'auth.hero.line2': { ar: 'في إنستغرام وماسنجر خلال ثوانٍ', en: 'on Instagram & Messenger in seconds' },
  'auth.hero.sub': { ar: 'يجيب على الأسعار، يبحث في منتجاتك، ويُغلق طلبات الدفع عند الاستلام تلقائياً — بالعربية.', en: 'Quotes prices, searches your catalog, and closes COD orders automatically — in Arabic.' },
  'auth.feature.1': { ar: 'ردود فورية ٢٤/٧', en: 'Instant replies, 24/7' },
  'auth.feature.2': { ar: 'بحث بالنص والصورة', en: 'Text & image product search' },
  'auth.feature.3': { ar: 'ردّ التعليقات تلقائياً', en: 'Auto-reply to comments' },
  'auth.email': { ar: 'البريد الإلكتروني', en: 'Email' },
  'auth.password': { ar: 'كلمة المرور', en: 'Password' },
  'auth.email.ph': { ar: 'you@example.com', en: 'you@example.com' },
  'auth.password.ph': { ar: '••••••••', en: '••••••••' },
  'auth.signinBtn': { ar: 'تسجيل الدخول', en: 'Sign in' },
  'auth.signupBtn': { ar: 'إنشاء الحساب', en: 'Create account' },
  'auth.noAccount': { ar: 'ليس لديك حساب؟', en: "Don't have an account?" },
  'auth.haveAccount': { ar: 'لديك حساب بالفعل؟', en: 'Already have an account?' },
  'auth.toSignup': { ar: 'أنشئ حساباً', en: 'Sign up' },
  'auth.toSignin': { ar: 'سجّل الدخول', en: 'Sign in' },
  'auth.or': { ar: 'أو', en: 'or' },
  'auth.google': { ar: 'المتابعة عبر Google', en: 'Continue with Google' },
  'auth.working': { ar: 'لحظة…', en: 'Working…' },
  'auth.forgot': { ar: 'نسيت كلمة المرور؟', en: 'Forgot password?' },
  'auth.verify.title': { ar: 'تأكيد بريدك', en: 'Verify your email' },
  'auth.verify.sub': { ar: 'أدخل الرمز الذي أرسلناه إلى بريدك', en: 'Enter the code we sent to your email' },
  'auth.verify.code': { ar: 'رمز التأكيد', en: 'Verification code' },
  'auth.verify.btn': { ar: 'تأكيد ومتابعة', en: 'Verify & continue' },
  'auth.verify.resend': { ar: 'إعادة إرسال الرمز', en: 'Resend code' },
  'auth.err.generic': { ar: 'حدث خطأ، حاول مجدداً', en: 'Something went wrong, try again' },
  'auth.err.notReady': { ar: 'جارٍ التحميل، انتظر لحظة', en: 'Still loading, one moment' },
  'auth.2fa.title': { ar: 'التحقق بخطوتين', en: 'Two-step verification' },
  'auth.2fa.totp.sub': { ar: 'افتح تطبيق المصادقة وأدخل الرمز الظاهر (لا يُرسَل رمز)', en: 'Open your authenticator app and enter the code shown (no code is sent)' },
  'auth.2fa.phone.sub': { ar: 'أدخل الرمز المُرسل إلى هاتفك عبر SMS', en: 'Enter the code sent to your phone via SMS' },
  'auth.2fa.email.sub': { ar: 'أدخل الرمز المُرسل إلى بريدك الإلكتروني', en: 'Enter the code sent to your email' },
  'auth.2fa.backup.sub': { ar: 'أدخل أحد رموز النسخ الاحتياطي', en: 'Enter one of your backup codes' },
  'auth.2fa.code': { ar: 'رمز التحقق', en: 'Verification code' },
  'auth.2fa.btn': { ar: 'تحقّق ودخول', en: 'Verify & sign in' },
  'auth.2fa.back': { ar: '← رجوع', en: '← Back' },

  // ─── Onboarding ───────────────────────────────────────────────────
  'onb.title': { ar: 'أهلاً بك في ChatRunner', en: 'Welcome to ChatRunner' },
  'onb.subtitle': { ar: 'أخبرنا عن نشاطك حتى نجهّز لك الأدوات المناسبة.', en: 'Tell us about your business so we set up the right tools.' },
  'onb.businessName': { ar: 'اسم النشاط التجاري', en: 'Business name' },
  'onb.businessName.ph': { ar: 'مثال: بوتيك ليلى', en: "e.g. Layla's Boutique" },
  'onb.typeQ': { ar: 'ما نوع نشاطك؟', en: 'What kind of business is it?' },
  'onb.err.signin': { ar: 'لم يتم تسجيل الدخول', en: 'Not signed in' },
  'onb.err.type': { ar: 'اختر نوع النشاط', en: 'Pick a business type' },
  'onb.err.name': { ar: 'أدخل اسم النشاط', en: 'Enter a business name' },
  'onb.type.direct.title': { ar: 'أبيع عبر رسائل إنستغرام/ماسنجر', en: 'I sell through Instagram/Messenger DMs' },
  'onb.type.direct.sub': { ar: 'كتالوج وطلبات مدمجة — بدون منصّة خارجية.', en: 'Built-in catalog & orders — no external platform.' },
  'onb.type.platform.title': { ar: 'عندي متجر على منصّة', en: 'I have a store on a platform' },
  'onb.type.platform.sub': { ar: 'سوق، أو أي منصّة تدعم ChatRunner (لاحقاً).', en: 'Sooqy, or any platform that supports ChatRunner (later).' },
  'onb.type.service.title': { ar: 'أقدّم خدمات', en: 'I run a service business' },
  'onb.type.service.sub': { ar: 'عيادة، صالون، نادي، عقارات، إلخ.', en: 'Clinic, salon, gym, real estate, etc.' },
  'onb.type.saas.title': { ar: 'نشاط رقمي أو SaaS', en: 'SaaS or digital business' },
  'onb.type.saas.sub': { ar: 'دعم العملاء عبر الرسائل.', en: 'Tier-1 customer support over DMs.' },
  'onb.type.hybrid.title': { ar: 'مزيج من الأعلى', en: 'A mix of the above' },
  'onb.type.hybrid.sub': { ar: 'فعّل الميزات حسب حاجتك.', en: 'Enable feature modules à la carte.' },
};

interface LangCtx {
  lang: Lang;
  dir: 'rtl' | 'ltr';
  setLang: (l: Lang) => void;
  toggle: () => void;
  t: (key: string) => string;
}

const Ctx = createContext<LangCtx | null>(null);
const STORAGE_KEY = 'chatrunner.lang';

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const saved = (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) as Lang | null;
    return saved === 'en' || saved === 'ar' ? saved : 'ar';
  });

  useEffect(() => {
    // Layout is locked LTR — sidebar always on the left, only strings switch.
    document.documentElement.lang = lang;
    document.documentElement.dir = 'ltr';
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
  }, [lang]);

  const value: LangCtx = {
    lang,
    dir: 'ltr',
    setLang: setLangState,
    toggle: () => setLangState((l) => (l === 'ar' ? 'en' : 'ar')),
    t: (key: string) => DICT[key]?.[lang] ?? key,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): LangCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useLang must be used within LangProvider');
  return c;
}

export function LangSwitcher({ className = '' }: { className?: string }) {
  const { toggle, t } = useLang();
  return (
    <button
      onClick={toggle}
      className={
        'inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:border-slate-400 transition ' +
        className
      }
      aria-label="Switch language"
    >
      {t('lang.switch')}
    </button>
  );
}
